const SHEET_ID = import.meta.env.VITE_SPREADSHEET_ID;
const TOKEN_URL = import.meta.env.VITE_GRETA_TOKEN_URL;
const CHAT_ID = import.meta.env.VITE_CHAT_ID;

let cachedToken = null;
let tokenExpiresAt = 0;
let sheetMeta = null;
let metadataRequest = null;

export async function getAccessToken() {
  if (cachedToken && Date.now() < tokenExpiresAt) return cachedToken;

  const response = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({chatId: CHAT_ID})
  });
  const result = await response.json();

  if (!response.ok || !result.success || !result.access_token) {
    throw new Error(result.error || 'Unable to get a Google Sheets access token.');
  }

  cachedToken = result.access_token;
  tokenExpiresAt = Date.now() + Math.max(0, (result.expires_in - 60) * 1000);
  return cachedToken;
}

export async function sheetsRequest(path, init = {}) {
  if (!SHEET_ID) throw new Error('Google Sheets is not configured.');

  let response;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const token = await getAccessToken();
    response = await fetch(
      `https://sheets.googleapis.com/v4/spreadsheets/${SHEET_ID}${path}`,
      {
        ...init,
        headers: {
          ...(init.headers || {}),
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.status !== 429 || attempt === 3) break;
    await new Promise((resolve) => setTimeout(resolve, 400 * (2 ** attempt)));
  }

  if (!response.ok) {
    throw new Error(
      `Google Sheets request failed (${response.status}): ${await response.text()}`
    );
  }

  return response.json();
}

export async function getRows(range) {
  const result = await sheetsRequest(`/values/${encodeURIComponent(range)}`);
  return result.values || [];
}

export async function appendRow(range, values) {
  return sheetsRequest(
    `/values/${encodeURIComponent(range)}:append?valueInputOption=USER_ENTERED&insertDataOption=INSERT_ROWS`,
    {method: 'POST', body: JSON.stringify({values: [values]})}
  );
}

export async function updateRow(range, values) {
  return sheetsRequest(
    `/values/${encodeURIComponent(range)}?valueInputOption=USER_ENTERED`,
    {method: 'PUT', body: JSON.stringify({values: [values]})}
  );
}

export async function getSheetMeta(force = false) {
  if (!force && sheetMeta) return sheetMeta;
  if (!force && metadataRequest) return metadataRequest;

  metadataRequest = sheetsRequest('/?fields=sheets.properties').then((result) => {
    sheetMeta = Object.fromEntries(
      (result.sheets || []).map((sheet) => [
        sheet.properties.title,
        sheet.properties.sheetId
      ])
    );
    return sheetMeta;
  });

  try {
    return await metadataRequest;
  } finally {
    metadataRequest = null;
  }
}

export async function getSheetIdByTitle(title) {
  let meta = await getSheetMeta();
  if (meta[title] === undefined) meta = await getSheetMeta(true);
  return meta[title];
}

function columnName(number) {
  let name = '';
  while (number > 0) {
    const remainder = (number - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    number = Math.floor((number - 1) / 26);
  }
  return name;
}

export async function ensureTab(title, headers) {
  let meta = await getSheetMeta();

  if (meta[title] === undefined) {
    await sheetsRequest('/:batchUpdate', {
      method: 'POST',
      body: JSON.stringify({requests: [{addSheet: {properties: {title}}}]})
    });
    sheetMeta = null;
    meta = await getSheetMeta(true);
  }

  const endColumn = columnName(headers.length);
  const existingHeaders = await getRows(`${title}!A1:${endColumn}1`);
  const current = existingHeaders[0] || [];

  if (current.length === 0) {
    await updateRow(`${title}!A1:${endColumn}1`, headers);
    return;
  }

  const canAppendHeaders = current.length < headers.length &&
    current.every((header, index) => header === headers[index]);

  if (canAppendHeaders) {
    await updateRow(`${title}!A1:${endColumn}1`, headers);
  }
}

export async function findRowIndexById(title, id) {
  const rows = await getRows(`${title}!A:A`);
  for (let index = 1; index < rows.length; index += 1) {
    if (rows[index]?.[0] === id) return index + 1;
  }
  return -1;
}

export async function deleteRow(title, id) {
  const sheetId = await getSheetIdByTitle(title);
  if (sheetId === undefined) throw new Error(`Sheet not found: ${title}`);

  const rowNumber = await findRowIndexById(title, id);
  if (rowNumber < 0) throw new Error(`Record not found in ${title}.`);

  const startIndex = rowNumber - 1;
  sheetMeta = null;

  return sheetsRequest('/:batchUpdate', {
    method: 'POST',
    body: JSON.stringify({
      requests: [{
        deleteDimension: {
          range: {
            sheetId,
            dimension: 'ROWS',
            startIndex,
            endIndex: startIndex + 1
          }
        }
      }]
    })
  });
}